import {
  AMENDMENT_KIND_DESCRIPTIONS,
  AMENDMENT_KIND_LABELS,
  type AmendmentKind,
  type HandoverDetail,
  startAmendmentSchema,
} from '@acasa/shared'
import { useState } from 'react'
import { useStartAmendment } from './use-handovers'

const KINDS: AmendmentKind[] = ['minor', 'major']

/**
 * Starting a change to a completed protocol.
 *
 * The kind is the person's call, not the tool's: whether a transposed digit is
 * a typo or a dispute is known on site. Nothing is preselected for the same
 * reason — "Kleine Korrektur" as a default would be the tool deciding that
 * the signatures still hold. The reason is mandatory and printed in the
 * Änderungshistorie, because a change to a signed record without one is not a
 * record.
 */
export function AmendDialog({
  protocol,
  onClose,
  onStarted,
}: {
  protocol: HandoverDetail
  onClose: () => void
  onStarted: () => void
}) {
  const start = useStartAmendment(protocol.id)
  const [kind, setKind] = useState<AmendmentKind | null>(null)
  const [reason, setReason] = useState('')

  const parsed = kind ? startAmendmentSchema.safeParse({ kind, reason }) : null
  const valid = parsed?.success === true

  const submit = async () => {
    if (!parsed?.success) return
    await start.mutateAsync(parsed.data)
    onStarted()
  }

  return (
    <div className="bg-ink/40 fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6">
      <div className="bg-surface border-line max-h-full w-full max-w-lg overflow-auto border p-5">
        <h2 className="font-display text-ink text-lg">Änderung vornehmen</h2>
        <p className="text-muted mt-2 text-xs leading-relaxed">
          Fassung {protocol.version} bleibt gültig, bis die Änderung als Fassung{' '}
          {protocol.version + 1} abgeschlossen wird.
        </p>

        <fieldset className="mt-4">
          <legend className="text-faint mb-2 text-[10px] tracking-[0.14em] uppercase">
            Art der Änderung
          </legend>
          <div className="grid gap-2">
            {KINDS.map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={kind === option}
                onClick={() => setKind(option)}
                className={`border px-3 py-2.5 text-left transition-colors ${
                  kind === option ? 'border-brand bg-brand-soft' : 'border-line hover:border-ink/40'
                }`}
              >
                <span
                  className={`block text-sm ${
                    kind === option ? 'text-brand font-medium' : 'text-ink'
                  }`}
                >
                  {AMENDMENT_KIND_LABELS[option]}
                </span>
                <span className="text-muted mt-0.5 block text-[11px] leading-relaxed">
                  {AMENDMENT_KIND_DESCRIPTIONS[option]}
                </span>
              </button>
            ))}
          </div>
        </fieldset>

        <label className="mt-5 block">
          <span className="text-faint mb-2 block text-[10px] tracking-[0.14em] uppercase">
            Begründung
          </span>
          <textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            maxLength={500}
            rows={3}
            placeholder="z. B. Zählernummer falsch abgelesen, Telefonnummer ergänzt"
            className="border-line focus:border-brand bg-canvas text-ink placeholder:text-faint w-full border px-3 py-2.5 text-sm outline-none"
          />
          <span className="text-faint mt-1 block text-[11px]">
            Wird in der Änderungshistorie des Protokolls abgedruckt.
          </span>
        </label>

        <p className="text-muted mt-4 text-[11px]">
          Die bisherige Fassung bleibt als PDF erhalten.
        </p>

        {start.isError && (
          <p className="text-danger mt-3 text-xs">
            {start.error instanceof Error ? start.error.message : 'Änderung konnte nicht beginnen'}
          </p>
        )}

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="border-line text-muted hover:text-ink border px-4 py-2.5 text-xs transition-colors"
          >
            Abbrechen
          </button>
          <button
            type="button"
            disabled={!valid || start.isPending}
            onClick={() => void submit().catch(() => undefined)}
            className="bg-brand text-surface hover:bg-brand-strong px-4 py-2.5 text-xs font-medium transition-colors disabled:opacity-40"
          >
            {start.isPending ? 'wird begonnen …' : 'Änderung beginnen'}
          </button>
        </div>
      </div>
    </div>
  )
}
